/**
 * IndexedDB 工具类，用于管理聊天会话存储
 */
class ConversationStorage {
  constructor() {
    this.dbName = 'AI_Merchant_DB';
    this.dbVersion = 5; // 升级版本以修复最后一条会话删除后刷新两次又恢复的问题
    this.storeName = 'conversations';
    this.guestStoreName = 'guest_conversation'; // 游客模式存储表
    this.db = null;
  }

  /**
   * 初始化数据库
   * @returns {Promise<IDBDatabase>} 数据库实例
   */
  async initDB() {
    return new Promise((resolve, reject) => {
      if (this.db) {
        resolve(this.db);
        return;
      }

      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = (event) => {
        console.error('IndexedDB open error:', event.target.error);
        reject(event.target.error);
      };

      request.onsuccess = (event) => {
        this.db = event.target.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // 先检查是否需要删除旧的对象存储并重新创建
        if (db.objectStoreNames.contains(this.storeName)) {
          // 删除旧的对象存储
          db.deleteObjectStore(this.storeName);
          console.log('Deleted old object store to recreate with proper indices for version 5');
        }
        
        // 创建新的普通用户会话存储对象
        const objectStore = db.createObjectStore(this.storeName, { keyPath: 'id' });
        // 创建索引以支持按时间排序
        objectStore.createIndex('updatedAt', 'updatedAt', { unique: false });
        // 创建索引以支持按用户ID过滤
        objectStore.createIndex('userId', 'userId', { unique: false });
        console.log('Created new object store with userId index for version 5');
        
        // 创建游客模式会话存储对象（如果不存在）
        if (!db.objectStoreNames.contains(this.guestStoreName)) {
          db.createObjectStore(this.guestStoreName, { keyPath: 'id' });
          console.log('Created guest conversation store');
        } else {
          // 如果游客存储已存在，也重新创建以确保一致性
          db.deleteObjectStore(this.guestStoreName);
          db.createObjectStore(this.guestStoreName, { keyPath: 'id' });
          console.log('Recreated guest conversation store for consistency');
        }
      };
    });
  }

  /**
   * 获取所有会话
   * @param {string} [userId] 用户ID，可选
   * @returns {Promise<Array>} 会话列表
   */
  async getAllConversations(userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        let request;

        if (userId) {
          console.log('Getting conversations for user:', userId);
          // 检查索引是否存在
          if (objectStore.indexNames.contains('userId')) {
            console.log('Using userId index');
            // 使用userId索引获取特定用户的会话
            const index = objectStore.index('userId');
            request = index.getAll(userId);
          } else {
            console.warn('userId index not found, getting all conversations and filtering manually');
            // 如果索引不存在，获取所有会话并手动过滤
            request = objectStore.getAll();
          }
        } else {
          console.log('Getting all conversations (no userId provided)');
          // 获取所有会话（兼容旧版本）
          request = objectStore.getAll();
        }

        request.onerror = (event) => {
          console.error('Failed to get conversations:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          let conversations = event.target.result || [];
          
          // 如果使用了索引，或者没有提供userId，直接返回结果
          if ((userId && objectStore.indexNames.contains('userId')) || !userId) {
            console.log('Fetched conversations:', conversations.length, 'conversations');
          } else {
            // 如果没有使用索引但提供了userId，手动过滤会话
            console.log('Filtering conversations manually for userId:', userId);
            console.log('All conversations before filtering:', conversations.length);
            conversations = conversations.filter(conversation => conversation.userId === userId);
            console.log('Conversations after filtering:', conversations.length);
          }
          
          // 按更新时间降序排序
          conversations.sort((a, b) => b.updatedAt - a.updatedAt);
          resolve(conversations);
        };
      });
    } catch (error) {
      console.error('Error getting conversations:', error);
      return [];
    }
  }

  /**
   * 保存单个会话
   * @param {Object} conversation 会话对象
   * @param {string} userId 用户ID
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveConversation(conversation, userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        // 先检查会话是否已存在
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const getRequest = objectStore.get(conversation.id);

        getRequest.onerror = (event) => {
          console.error('Failed to check existing conversation:', event.target.error);
          reject(event.target.error);
        };

        getRequest.onsuccess = (event) => {
          const existingConversation = event.target.result;
          let conversationToSave;

          if (existingConversation) {
            // 会话已存在，保留原有的userID，只更新其他字段
            conversationToSave = {
              ...existingConversation,
              title: conversation.title,
              preview: conversation.preview,
              messages: conversation.messages,
              updatedAt: conversation.updatedAt
            };
          } else {
            // 会话不存在，添加新会话并设置userID
            conversationToSave = { ...conversation, userId };
          }

          // 保存会话
          const putRequest = objectStore.put(conversationToSave);

          putRequest.onerror = (event) => {
            console.error('Failed to save conversation:', event.target.error);
            reject(event.target.error);
          };

          putRequest.onsuccess = () => {
            resolve(true);
          };
        };
      });
    } catch (error) {
      console.error('Error saving conversation:', error);
      return false;
    }
  }

  /**
   * 保存多个会话
   * @param {Array} conversations 会话数组
   * @param {string} [userId] 用户ID，可选
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveConversations(conversations, userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        
        let completed = 0;
        let hasError = false;
        
        // 保存每个会话
        conversations.forEach(conversation => {
          // 先检查会话是否已存在
          const getRequest = objectStore.get(conversation.id);
          
          getRequest.onerror = (event) => {
            console.error('Failed to check existing conversation:', event.target.error);
            hasError = true;
            completed++;
            if (completed === conversations.length) {
              resolve(!hasError);
            }
          };
          
          getRequest.onsuccess = (event) => {
            const existingConversation = event.target.result;
            let conversationToSave;
            
            if (existingConversation) {
              // 会话已存在，保留原有的userID和其他字段，只更新必要的字段
              conversationToSave = {
                ...existingConversation,
                title: conversation.title || existingConversation.title,
                preview: conversation.preview || existingConversation.preview,
                messages: conversation.messages || existingConversation.messages,
                updatedAt: conversation.updatedAt || existingConversation.updatedAt
              };
            } else {
              // 会话不存在，添加新会话并设置userID（如果提供）
              conversationToSave = userId ? { ...conversation, userId } : conversation;
            }
            
            // 保存会话
            const putRequest = objectStore.put(conversationToSave);
            
            putRequest.onerror = (event) => {
              console.error('Failed to save conversation:', event.target.error);
              hasError = true;
            };
            
            putRequest.onsuccess = () => {
              completed++;
              if (completed === conversations.length) {
                resolve(!hasError);
              }
            };
          };
        });
      });
    } catch (error) {
      console.error('Error saving conversations:', error);
      return false;
    }
  }

  /**
   * 获取单个会话
   * @param {string} conversationId 会话ID
   * @param {string} [userId] 用户ID，可选，用于验证会话归属
   * @returns {Promise<Object|null>} 会话对象或null
   */
  async getConversation(conversationId, userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readonly');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.get(conversationId);

        request.onerror = (event) => {
          console.error('Failed to get conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          const conversation = event.target.result;
          
          // 如果提供了用户ID，验证会话归属
          if (conversation && userId) {
            if (conversation.userId === userId) {
              resolve(conversation);
            } else {
              // 会话不属于该用户
              console.warn('Conversation does not belong to the specified user');
              resolve(null);
            }
          } else {
            // 没有提供用户ID或会话不存在
            resolve(conversation || null);
          }
        };
      });
    } catch (error) {
      console.error('Error getting conversation:', error);
      return null;
    }
  }

  /**
   * 删除会话
   * @param {string} conversationId 会话ID
   * @param {string} [userId] 用户ID，可选，用于验证会话归属
   * @returns {Promise<boolean>} 是否删除成功
   */
  async deleteConversation(conversationId, userId) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        console.log('Deleting conversation:', conversationId, 'for user:', userId);
        
        // 使用读写事务进行删除操作
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);

        // 先获取会话以验证归属（如果提供了用户ID）
        if (userId) {
          const getRequest = objectStore.get(conversationId);

          getRequest.onerror = (event) => {
            console.error('Failed to get conversation for deletion:', event.target.error);
            reject(event.target.error);
          };

          getRequest.onsuccess = (event) => {
            const conversation = event.target.result;
            
            if (!conversation) {
              // 会话不存在
              console.warn('Conversation not found for deletion:', conversationId);
              resolve(false);
            } else if (conversation.userId !== userId) {
              // 会话不属于该用户
              console.warn('Cannot delete conversation that does not belong to the specified user. Expected:', userId, 'Got:', conversation.userId);
              resolve(false);
            } else {
              // 会话属于该用户，可以删除
              console.log('Deleting conversation:', conversationId, 'belonging to user:', userId);
              const deleteRequest = objectStore.delete(conversationId);

              deleteRequest.onerror = (event) => {
                console.error('Failed to delete conversation:', event.target.error);
                reject(event.target.error);
              };

              deleteRequest.onsuccess = () => {
                console.log('Successfully deleted conversation:', conversationId);
                resolve(true);
              };
            }
          };
        } else {
          // 没有提供用户ID，直接删除
          console.log('Deleting conversation without user ID check:', conversationId);
          const deleteRequest = objectStore.delete(conversationId);

          deleteRequest.onerror = (event) => {
            console.error('Failed to delete conversation:', event.target.error);
            reject(event.target.error);
          };

          deleteRequest.onsuccess = () => {
            console.log('Successfully deleted conversation:', conversationId);
            resolve(true);
          };
        }
      });
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return false;
    }
  }

  /**
   * 清空所有会话
   * @returns {Promise<boolean>} 是否清空成功
   */
  async clearAllConversations() {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.storeName], 'readwrite');
        const objectStore = transaction.objectStore(this.storeName);
        const request = objectStore.clear();

        request.onerror = (event) => {
          console.error('Failed to clear conversations:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error clearing conversations:', error);
      return false;
    }
  }

  /**
   * 从localStorage迁移数据到IndexedDB
   * @returns {Promise<boolean>} 是否迁移成功
   */
  async migrateFromLocalStorage() {
    try {
      // 使用更可靠的迁移标记，包含版本号
      const migrationKey = 'chat_conversations_migrated_v5';
      
      // 检查localStorage中是否有数据
      const localStorageData = localStorage.getItem('chat_conversations');
      if (!localStorageData) {
        // 没有数据需要迁移，确保迁移标记存在
        localStorage.setItem(migrationKey, 'true');
        return true;
      }

      // 检查是否已经迁移过（使用新版本的迁移标记）
      const hasMigrated = localStorage.getItem(migrationKey);
      if (hasMigrated) {
        // 已经迁移过，直接清除localStorage数据
        localStorage.removeItem('chat_conversations');
        // 清理旧的迁移标记
        localStorage.removeItem('chat_conversations_migrated');
        return true;
      }

      // 检查是否使用旧版本的迁移标记迁移过
      const hasOldMigrated = localStorage.getItem('chat_conversations_migrated');
      if (hasOldMigrated) {
        // 已经使用旧版本迁移过，直接清除数据和旧标记
        localStorage.removeItem('chat_conversations');
        localStorage.removeItem('chat_conversations_migrated');
        localStorage.setItem(migrationKey, 'true');
        return true;
      }

      const conversations = JSON.parse(localStorageData);
      if (conversations.length === 0) {
        localStorage.removeItem('chat_conversations');
        localStorage.setItem(migrationKey, 'true');
        return true;
      }

      console.log('Starting migration from localStorage to IndexedDB for version 5');
      
      // 注意：不再自动保存到IndexedDB，因为这可能导致已删除的会话重新出现
      // 只清除localStorage数据并设置迁移标记
      
      // 清除localStorage数据
      localStorage.removeItem('chat_conversations');
      // 清理旧的迁移标记
      localStorage.removeItem('chat_conversations_migrated');
      // 设置新版本的迁移标记
      localStorage.setItem(migrationKey, 'true');
      
      console.log('Migration completed: localStorage data cleared, will not auto-save to avoid re-adding deleted conversations');
      
      return true;
    } catch (error) {
      console.error('Error migrating from localStorage:', error);
      
      // 即使出现错误，也要标记为已迁移并清除localStorage数据
      // 这样可以避免重复迁移已删除的会话
      const migrationKey = 'chat_conversations_migrated_v5';
      localStorage.setItem(migrationKey, 'true');
      localStorage.removeItem('chat_conversations');
      localStorage.removeItem('chat_conversations_migrated');
      
      return false;
    }
  }
  
  /**
   * 获取游客会话
   * @returns {Promise<Object|null>} 游客会话对象或null
   */
  async getGuestConversation() {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.guestStoreName], 'readonly');
        const objectStore = transaction.objectStore(this.guestStoreName);
        const request = objectStore.getAll();

        request.onerror = (event) => {
          console.error('Failed to get guest conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = (event) => {
          const conversations = event.target.result || [];
          // 游客模式下只应该有一个会话，返回第一个
          resolve(conversations.length > 0 ? conversations[0] : null);
        };
      });
    } catch (error) {
      console.error('Error getting guest conversation:', error);
      return null;
    }
  }
  
  /**
   * 保存游客会话
   * @param {Object} conversation 会话对象
   * @returns {Promise<boolean>} 是否保存成功
   */
  async saveGuestConversation(conversation) {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.guestStoreName], 'readwrite');
        const objectStore = transaction.objectStore(this.guestStoreName);
        
        // 先清空现有数据，确保游客模式只有一个会话
        const clearRequest = objectStore.clear();
        
        clearRequest.onerror = (event) => {
          console.error('Failed to clear guest conversations:', event.target.error);
          reject(event.target.error);
        };
        
        clearRequest.onsuccess = () => {
          // 添加新会话
          const addRequest = objectStore.add(conversation);
          
          addRequest.onerror = (event) => {
            console.error('Failed to save guest conversation:', event.target.error);
            reject(event.target.error);
          };
          
          addRequest.onsuccess = () => {
            resolve(true);
          };
        };
      });
    } catch (error) {
      console.error('Error saving guest conversation:', error);
      return false;
    }
  }
  
  /**
   * 删除游客会话
   * @returns {Promise<boolean>} 是否删除成功
   */
  async deleteGuestConversation() {
    try {
      const db = await this.initDB();
      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.guestStoreName], 'readwrite');
        const objectStore = transaction.objectStore(this.guestStoreName);
        const request = objectStore.clear();

        request.onerror = (event) => {
          console.error('Failed to delete guest conversation:', event.target.error);
          reject(event.target.error);
        };

        request.onsuccess = () => {
          resolve(true);
        };
      });
    } catch (error) {
      console.error('Error deleting guest conversation:', error);
      return false;
    }
  }
  
  /**
   * 清空所有游客会话
   * @returns {Promise<boolean>} 是否清空成功
   */
  async clearGuestConversation() {
    return this.deleteGuestConversation();
  }
}

// 创建单例实例
const conversationStorage = new ConversationStorage();

export default conversationStorage;